import { useContext, useEffect, useMemo, useState } from "react";
import { BreadTransaction, P, Predicate } from "breadcommon";
import { RyeDropDownMultiSelect } from "../rye/RyeDropDownMultiSelect";
import { DropDownEntry } from "../rye/RyeDropDown";
import { TransactionColumn } from "./TransactionColumn";
import { TransactionsNavContext } from "./TransactionsScreen";
import { TransactionColumnFilterProps } from "./DefaultTransactionColumnHeader";
import { useMerchants } from "../derived/useMerchants";
import { MerchantLogo } from "../common/MerchantLogo";
import { buildClasses } from "../utils/buildClasses";

export function TransactionColumnFilterMerchant({
  isOpen,
  onClose,
  hasActiveFilter,
}: TransactionColumnFilterProps): JSX.Element | null {
  const [transactionsNav, setTransactionsNav] = useContext(
    TransactionsNavContext,
  );
  const merchants = useMerchants();
  const [selectedMerchantNames, setSelectedMerchantNames] = useState<string[]>(
    [],
  );

  useEffect(() => {
    if (!hasActiveFilter) {
      setSelectedMerchantNames([]);
    }
  }, [hasActiveFilter]);

  function createMerchantPredicate(names: string[]): Predicate | null {
    if (names.length === 0) {
      return null;
    }
    const field = P.NestedField<BreadTransaction, "merchant", "name">(
      "merchant",
      "name",
    );
    const predicates = names.map((name) =>
      P.EqualsCI(field, P.Literal(name)),
    );
    return P.Any(...predicates);
  }

  function applyFilter(names: string[]) {
    const currentSettings =
      transactionsNav.columnSettings[TransactionColumn.MERCHANT];
    setTransactionsNav((prevState) => ({
      ...prevState,
      columnSettings: {
        ...transactionsNav.columnSettings,
        [TransactionColumn.MERCHANT]: {
          ...currentSettings,
          predicate: createMerchantPredicate(names),
        },
      },
    }));
  }

  const entries: DropDownEntry<string>[] = useMemo(() => {
    return Array.from(merchants.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((merchant) => ({
        id: merchant.name,
        text: merchant.name,
        icon: (
          <div className={buildClasses("flex", "items-center")}>
            <MerchantLogo merchant={merchant} size={18} />
          </div>
        ),
      }));
  }, [merchants]);

  function handleSelectionChange(names: string[]) {
    setSelectedMerchantNames(names);
    applyFilter(names);
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className={buildClasses("flex", "flex-col", "gap-2")}>
      <RyeDropDownMultiSelect
        selectedEntryIds={selectedMerchantNames}
        onSelectionChange={handleSelectionChange}
        onEsc={onClose}
        entries={entries}
        searchable
        size="md"
        width="lg"
      />
    </div>
  );
}
